import { useEffect, useState } from "react";
import { useToast } from "native-base";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";

import { CREATE_MODALS } from "global/constants";
import { getStore } from "store/storeUtils";
import { apiSubmitScheduling, apiWipeRecordsScheduled, fetchSdRequestList } from "api/apiCall";
import { SD_LANDING_PAGE } from "global/paths";
import { showCustomToast, showErrorToast } from "utils/utilities";
import colors from "theme/colors";

const useSdScheduledData = () => {
  // contants destructuring
  const { modal, setModal, resetLoginData, setIsFileUploading } = getStore();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { requestId = "" } = location?.state || {};
  // use states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState("salaryDisbursement.table.verifiedTab");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllRowSelected, setIsAllRowSelected] = useState(false);
  const [excludedRows, setExcludedRows] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const tabs = ["salaryDisbursement.table.verifiedTab", "salaryDisbursement.table.rejectedTab"];

  // API calls
  const { data: sdRequestVerifiedListData = {}, mutate: getSdRequestVerifiedList } = useMutation({
    mutationFn: (params) => fetchSdRequestList({ requestId, params }),
  });

  const {
    isSuccess: sdRequestRejectedListIsSuccess,
    data: sdRequestRejectedListData = {},
    mutate: getSdRequestRejectedList,
  } = useMutation({
    mutationFn: (params) => fetchSdRequestList({ requestId, params }),
  });

  const {
    isSuccess: isSubmitSchedulingSuccess,
    isError: isSubmitSchedulingError,
    error: submitSchedulingError,
    mutate: submitIdsForScheduling,
  } = useMutation({ mutationFn: (data) => apiSubmitScheduling(data) });

  const {
    isSuccess: isWipeRecordsSuccess,
    isError: isWipeRecordsError,
    error: wipeRecordsError,
    mutate: wipeRecords,
  } = useMutation({ mutationFn: (data) => apiWipeRecordsScheduled(data) });

  // Use Effects
  const fetchData = (pageNo = 1) => {
    getSdRequestRejectedList({
      status: "CREATED",
      limit: 50,
      skip: (pageNo - 1) * 50,
      isVerified: false,
    });
    getSdRequestVerifiedList({
      status: "CREATED",
      limit: 50,
      skip: (pageNo - 1) * 50,
      isVerified: true,
      sendAmount: true,
    });
  };

  useEffect(() => {
    fetchData(currentPage);
    if (requestId !== "") {
      setIsFileUploading(true);
    }

    return () => {
      setIsFileUploading(false);
    };
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 1 && (sdRequestRejectedListData?.count || 0) > 0 && !isDataLoaded) {
      setSelectedTab("salaryDisbursement.table.rejectedTab");
      setIsDataLoaded(true);
    }
  }, [sdRequestRejectedListIsSuccess]);

  useEffect(() => {
    if (isSubmitSchedulingSuccess) {
      showCustomToast({
        title: "salaryDisbursement.tooltip.submitScheduleSuccess",
        status: "success",
        variant: "solid",
        toast,
      });
      setSelectedRows([]);
      navigate(SD_LANDING_PAGE);
    }

    if (isSubmitSchedulingError) {
      showErrorToast({
        error: submitSchedulingError,
        toast,
        navigate,
        resetLoginData,
      });
    }
  }, [isSubmitSchedulingSuccess, isSubmitSchedulingError]);

  useEffect(() => {
    if (isWipeRecordsSuccess) {
      showCustomToast({
        title: "salaryDisbursement.tooltip.dataIsDeleted",
        status: "success",
        variant: "solid",
        toast,
      });
      setSelectedRows([]);
      setExcludedRows([]);
      setIsAllRowSelected(false);
      setCurrentPage(1);
      fetchData();
    }

    if (isWipeRecordsError) {
      showErrorToast({
        error: wipeRecordsError,
        toast,
        navigate,
        resetLoginData,
      });
    }
  }, [isWipeRecordsSuccess, isWipeRecordsError]);

  // Constants
  const currentData = selectedTab.includes("verifiedTab") ? sdRequestVerifiedListData : sdRequestRejectedListData;

  // Function Calls
  const submitScheduling = () => {
    const shouldButtonsBeDisabled = selectedRows.length === 0;
    if (shouldButtonsBeDisabled && !isAllRowSelected) {
      showCustomToast({
        title: "salaryDisbursement.tooltip.pleaseSelectEmployees",
        status: "warning",
        variant: "outline",
        style: {
          backgroundColor: colors.warning.gold,
          borderColor: colors.warning.sunflower,
          borderWidth: 1,
        },
        toast,
      });
    } else {
      setModal({
        ...modal,
        type: CREATE_MODALS.sdSubmitSchedule,
      });
    }
  };

  const wipeSdScheduling = () => {
    const shouldButtonsBeDisabled = selectedRows.length === 0;
    if (shouldButtonsBeDisabled && !isAllRowSelected) {
      showCustomToast({
        title: "salaryDisbursement.tooltip.pleaseSelectEmployeesToDelete",
        status: "warning",
        variant: "outline",
        style: {
          backgroundColor: colors.warning.gold,
          borderColor: colors.warning.sunflower,
          borderWidth: 1,
        },
        toast,
      });
    } else {
      setModal({
        ...modal,
        type: CREATE_MODALS.WipeSdScheduling,
      });
    }
  };

  const handleSendData = () => {
    submitIdsForScheduling({
      recordIds: isAllRowSelected ? [] : selectedRows.map((row) => row?.id),
      requestId,
      excludedIds: excludedRows.map((row) => row?.id),
    });
  };

  const handleWipe = () => {
    wipeRecords({
      recordIds: isAllRowSelected ? [] : selectedRows.map((row) => row?.id),
      requestId,
      excludedIds: excludedRows.map((row) => row?.id),
    });
  };

  return {
    sdRequestVerifiedListData,
    getSdRequestVerifiedList,
    sdRequestRejectedListData,
    getSdRequestRejectedList,
    submitScheduling,
    wipeSdScheduling,
    handleSendData,
    handleWipe,
    currentPage,
    setCurrentPage,
    selectedTab,
    setSelectedTab,
    selectedRows,
    setSelectedRows,
    tabs,
    isAllRowSelected,
    setIsAllRowSelected,
    currentData,
    modal,
    setModal,
    requestId,
    excludedRows,
    setExcludedRows,
  };
};

export default useSdScheduledData;
